import { ActionIcon, Badge, Group, Select, Text, Tooltip } from "@mantine/core";
import { modals } from "@mantine/modals";
import { IconCircleCheck, IconCircleX } from "@tabler/icons-react";
import { useMutation } from "@tanstack/react-query";
import { useSnapshot } from "valtio";
import type { allowedEmailTexts } from "@/locales/allowed-email";
import { authStore } from "../../store/auth";
import {
	type AllowedEmailDto,
	setUserVerified,
	updateUserRole,
} from "./allowed-email.api";

// Cermin ROLES di utils/permission.ts (modul itu menarik Prisma, tidak aman di browser).
const ROLE_OPTIONS = ["admin", "user"] as const;

type Texts = (typeof allowedEmailTexts)["id"];

interface Props {
	user: NonNullable<AllowedEmailDto["user"]>;
	text: Texts;
	onChanged: (message: string) => void;
	onError: (error: Error) => void;
}

/** Kontrol ubah role & atur verifikasi untuk user yang memakai entri allowlist. */
export function AllowedEmailUserActions({
	user,
	text,
	onChanged,
	onError,
}: Props) {
	const snap = useSnapshot(authStore);
	const isSelf = user.id === snap.user?.id;

	const changeRole = useMutation({
		mutationFn: (role: string) => updateUserRole(user.id, role),
		onSuccess: () => onChanged(text.roleUpdatedOk),
		onError,
	});
	const toggleVerify = useMutation({
		mutationFn: (verified: boolean) => setUserVerified(user.id, verified),
		onSuccess: (_, verified) =>
			onChanged(verified ? text.verifiedOk : text.unverifiedOk),
		onError,
	});

	const confirmRole = (role: string | null) => {
		if (!role || role === user.role) return;
		modals.openConfirmModal({
			title: text.roleConfirmTitle,
			children: (
				<Text size="sm">{text.roleConfirmBody.replace("{role}", role)}</Text>
			),
			labels: { confirm: text.confirm, cancel: text.cancel },
			onConfirm: () => changeRole.mutate(role),
		});
	};

	const nextVerified = !user.emailVerified;
	const confirmVerify = () =>
		modals.openConfirmModal({
			title: nextVerified ? text.verifyConfirmTitle : text.unverifyConfirmTitle,
			children: (
				<Text size="sm">
					{nextVerified ? text.verifyConfirmBody : text.unverifyConfirmBody}
				</Text>
			),
			labels: { confirm: text.confirm, cancel: text.cancel },
			confirmProps: { color: nextVerified ? "green" : "red" },
			onConfirm: () => toggleVerify.mutate(nextVerified),
		});

	return (
		<Group gap="xs" wrap="nowrap">
			<Tooltip label={text.selfRoleHint} disabled={!isSelf}>
				<Select
					size="xs"
					w={110}
					aria-label={text.roleLabel}
					data={ROLE_OPTIONS.map((r) => ({ value: r, label: text.roles[r] }))}
					value={user.role}
					onChange={confirmRole}
					disabled={isSelf || changeRole.isPending}
					allowDeselect={false}
				/>
			</Tooltip>
			<Badge color={user.emailVerified ? "green" : "yellow"} variant="light">
				{user.emailVerified ? text.verified : text.unverified}
			</Badge>
			<Tooltip label={nextVerified ? text.verifyAction : text.unverifyAction}>
				<ActionIcon
					variant="light"
					color={nextVerified ? "green" : "red"}
					aria-label={nextVerified ? text.verifyAction : text.unverifyAction}
					loading={toggleVerify.isPending}
					onClick={confirmVerify}
				>
					{nextVerified ? (
						<IconCircleCheck size={16} />
					) : (
						<IconCircleX size={16} />
					)}
				</ActionIcon>
			</Tooltip>
		</Group>
	);
}
